import { BarTooltipProps, ComputedDatum, ResponsiveBar } from "@nivo/bar";
import { ReactNode, useCallback, useMemo } from "react";

import { Graphs } from "../types";
import { measureTextWidth } from "../utils";

export type ModalContentProps = {
  data: BarChartData[];
  translations: Graphs;
  isMobile?: boolean;
  tooltip: ({ value }: { value: BarDatum }) => ReactNode;
  onHover?: (
    datum: ComputedDatum<BarDatum>,
    event: React.MouseEvent<Element>,
  ) => void;
};

type ClientId = string;
type BarDatum = Omit<BarChartData, "clients">;

export type BarChartData = {
  label: string;
  color: string;
  clients: ClientId[];
  clientCount?: number;
  volume: string;
  suffix: string;
};

const barHeight = 58;
const labelTextColor = "rgba(98, 111, 134, 1)";
const defaultBarColor = "#e8c0a0";

export const BarChart = ({
  data,
  tooltip,
  translations,
  onHover,
  isMobile = false,
}: ModalContentProps) => {
  const barDatum = useMemo(
    () => [...data].reverse().filter((e) => parseFloat(e.volume) > 0),
    [data],
  );
  const chartHeight = barDatum.length * (isMobile ? barHeight - 20 : barHeight);

  const tooltipHandler = useCallback(
    (props: BarTooltipProps<BarDatum>) => {
      return tooltip({ value: props.data as BarChartData });
    },
    [tooltip],
  );

  const chartMargins = useMemo(() => {
    const TICK_PADDING = 10;
    const LABEL_OFFSET = 6;

    const maxLabelWidth = Math.max(
      ...data.map((item) => measureTextWidth(item.label)),
    );

    const maxValueWidth = Math.max(
      ...data.map((item) =>
        measureTextWidth(
          `${item.clientCount} ${translations.clients.toLowerCase()}`,
        ),
      ),
    );

    return {
      leftMargin: Math.ceil(maxLabelWidth) + TICK_PADDING,
      rightMargin: Math.ceil(maxValueWidth) + LABEL_OFFSET,
    };
  }, [data, translations]);

  const { leftMargin, rightMargin } = chartMargins;

  return (
    <div data-testid="bar-chart" style={{ height: chartHeight, width: "100%" }}>
      <ResponsiveBar
        data={barDatum as BarDatum[]}
        keys={["clientCount"]}
        indexBy="label"
        layout="horizontal"
        margin={{ top: 0, right: rightMargin, bottom: 0, left: leftMargin }}
        padding={0.4}
        innerPadding={4}
        colors={({ index }) =>
          barDatum[index].color.toString() || defaultBarColor
        }
        enableGridY={false}
        axisLeft={{ tickSize: 0, tickPadding: 10 }}
        axisBottom={null}
        valueFormat={(value) =>
          !!value ? value + " " + translations.clients.toLowerCase() : ""
        }
        onMouseEnter={onHover}
        onClick={onHover}
        tooltip={tooltipHandler}
        labelPosition="end"
        labelOffset={6}
        labelSkipWidth={0}
        labelTextColor={labelTextColor}
        borderRadius={4}
        theme={{
          tooltip: { container: { fontSize: "14px" } },
          text: { fontSize: "14px" },
          axis: {
            ticks: {
              text: {
                fontSize: "14px",
                fill: labelTextColor,
                fontWeight: 400,
                textTransform: "uppercase",
              },
            },
          },
        }}
      />
    </div>
  );
};
